/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import request from 'apmui/util/request';
import VdiClientInfo, { LaunchFunction, VDI_CLIENT } from './VdiClientInfo';
import VdiError, { VdiErrorType } from './VdiError';
import VmwareViewModel from './VmwareViewModel';

const NETWORK_REQUEST_TIMEOUT = 15000;

const clients: VdiClientInfo = {
    [VDI_CLIENT.NATIVE]: {
        name: 'VMware Horizon',
        downloadUrl:
            'https://customerconnect.omnissa.com/downloads/info/slug/virtual_desktop_and_apps/omnissa_horizon_clients/8',
        launch: launchNativeClient as LaunchFunction,
    },
    [VDI_CLIENT.HTML5]: {
        name: 'HTML5',
        launch: launchHtml5Client as LaunchFunction,
    },
};

async function getClients() {
    return clients;
}

function setVmviewHtml5Parameters(response: object, appToLaunch: VmwareViewModel) {
    const params = {};
    const responseKeys = ['desktopId', 'user', 'domainName', 'blastURL', 'brokerUrl'];

    responseKeys.forEach((key) => {
        params[key] = response[key];
    });

    params['idleTimeout'] = -1;
    params['userActivityXMLSendingInterval'] = '24000';
    params['brokerSupportIdleTimeout'] = true;
    params['desktopName'] = appToLaunch.caption;
    params['referer'] = document.location.href;
    params['rebrand'] = undefined;
    params['brokerUrlWithTimestamp'] = false;
    params['isApplicationSession'] = false;
    params['closeOnExit'] = true;

    // Extra parameters for View HTML5
    const originDesktop = {
        id: params['desktopId'],
        name: params['desktopName'],
    };
    params['SSS_OriginDesktop'] = JSON.stringify(originDesktop);

    const runningSession = {
        key: params['desktopId'],
        name: params['desktopName'],
        url: params['blastURL'].replace('https://', 'wss://'),
        isActive: true,
        isApplicationSession: false,
    };
    const sessions = [runningSession];
    params['SSS_RunningSession'] = JSON.stringify(sessions);

    const brokerUrlInfo = {
        url: params['brokerUrl'],
        urlParam: null,
    };
    params['SSS_BrokerUrlInfo'] = JSON.stringify(brokerUrlInfo);

    params['SSS_OriginAppID'] = '';
    params['SSS_TriedSSLVerify'] = '{}';
    params['SSS_ClientInfo'] = '{}';

    const entitledItems = {
        name: 'GetLaunchItems',
        timerInfo: {
            idleTimeout: params['idleTimeout'],
            userActivityXMLSendingInterval: params['userActivityXMLSendingInterval'],
        },
        content: {
            brokerUrl: params['brokerUrl'],
            desktops: {},
            applications: {},
            applicationSessions: {},
        },
    };
    params['SSS_EntitledItems'] = JSON.stringify(entitledItems);

    return params;
}

function launchHtml5Client(appToLaunch: VmwareViewModel) {
    if (typeof appToLaunch.windowName !== 'undefined') {
        // open HTML5 client immediately
        window.open(appToLaunch.html5ClientUri, appToLaunch.windowName);
        return;
    }

    return request(appToLaunch.html5ClientUri!, { timeout: NETWORK_REQUEST_TIMEOUT })
        .then((xhr) => JSON.parse(xhr.responseText))
        .then((response) => {
            if (response.result !== 'ok') {
                throw new VdiError(VdiErrorType.APP_LAUNCH, response.message);
            }

            if (response.version === 1) {
                window.open(response.url);
            } else {
                const html5Window = window.open('', '')!;
                const sessStorage = html5Window.sessionStorage;
                const params = setVmviewHtml5Parameters(response, appToLaunch);

                for (const key in params) {
                    if (params.hasOwnProperty(key)) {
                        const value = params[key];
                        sessStorage.setItem(key, value);
                    }
                }
                html5Window.name = '../1.0/iframe.html?t=' + new Date().getTime();
                html5Window.document.location.href = response.html5ClientUrl;
            }
        })
        .catch((reason) => {
            if (!(reason instanceof VdiError)) {
                reason = new VdiError(VdiErrorType.APP_LAUNCH, reason);
            }
            throw reason;
        });
}

function launchNativeClient(resource: VmwareViewModel) {
    window.open(resource.nativeClientUri, '_self');
    return Promise.resolve(true);
}

export { getClients as default, launchHtml5Client, launchNativeClient };
